"use client";
import { adminFetch } from "./session";

import { useMemo, useRef, useState } from "react";
import {
  Award,
  CalendarDays,
  Check,
  FileText,
  ImagePlus,
  Images,
  LayoutTemplate,
  LoaderCircle,
  Medal,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import type { ContentKind, ContentRecord, ContentStatus } from "@/lib/content-types";

const kindDetails: Record<ContentKind, { label: string; singular: string; icon: typeof CalendarDays }> = {
  result: { label: "Hírek és eredmények", singular: "bejegyzés", icon: CalendarDays },
  athlete: { label: "Versenyzők", singular: "versenyző", icon: Medal },
  media: { label: "Képek és videók", singular: "médiaelem", icon: Images },
  partner: { label: "Támogatók", singular: "támogató", icon: Award },
  grant: { label: "Pályázatok", singular: "pályázat", icon: FileText },
  page: { label: "Oldalak fő szövegei", singular: "oldal", icon: LayoutTemplate },
};

const kindOrder: ContentKind[] = ["result", "athlete", "media", "partner", "grant", "page"];

function value(record: ContentRecord, key: string): string {
  const item = record.data[key];
  return typeof item === "string" ? item : "";
}

function boolValue(record: ContentRecord, key: string): boolean {
  return record.data[key] === true;
}

function linesValue(record: ContentRecord, key: string): string {
  const item = record.data[key];
  return Array.isArray(item) ? item.map(String).join("\n") : "";
}

function emptyRecord(kind: ContentKind, sortOrder: number): ContentRecord {
  const dataByKind: Record<ContentKind, Record<string, unknown>> = {
    result: { date: "", place: "", lead: "", results: [], featured: false },
    athlete: { initials: "", category: "", focus: "", color: "blue", highlights: [], image: "", alt: "" },
    media: { mediaType: "image", label: "", image: "", videoUrl: "", alt: "", caption: "", credit: "", source: "", license: "", licenseUrl: "" },
    partner: { mark: "", role: "", website: "", image: "", alt: "" },
    grant: { badge: "", reference: "", goal: "", areas: [], website: "" },
    page: { slug: "", eyebrow: "", description: "", body: "" },
  };
  return { id: "", kind, title: "", status: "draft", sortOrder, data: dataByKind[kind] };
}

export function AdminDashboard({ initialRecords }: { initialRecords: ContentRecord[] }) {
  const [records, setRecords] = useState(initialRecords);
  const [activeKind, setActiveKind] = useState<ContentKind>("result");
  const [editor, setEditor] = useState<ContentRecord | null>(null);
  const [saving, setSaving] = useState(false);

  const visibleRecords = useMemo(
    () => records.filter((record) => record.kind === activeKind).sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title, "hu")),
    [records, activeKind],
  );

  function changeKind(kind: ContentKind) {
    setActiveKind(kind);
    setEditor(null);
  }

  function startNew() {
    const maxOrder = visibleRecords.reduce((max, item) => Math.max(max, item.sortOrder), -1);
    setEditor(emptyRecord(activeKind, maxOrder + 1));
  }

  function updateData(key: string, next: unknown) {
    setEditor((current) => current ? { ...current, data: { ...current.data, [key]: next } } : current);
  }

  async function save() {
    if (!editor || !editor.title.trim()) {
      toast.error("A cím vagy név kitöltése kötelező.");
      return;
    }
    setSaving(true);
    try {
      const response = await adminFetch("/api.php?action=content", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(editor),
      });
      const result = await response.json() as { record?: ContentRecord; error?: string };
      if (!response.ok || !result.record) throw new Error(result.error || "A mentés nem sikerült.");
      const savedRecord = { ...result.record, isDefault: editor.isDefault };
      setRecords((current) => [...current.filter((item) => item.id !== savedRecord.id), savedRecord]);
      setEditor(savedRecord);
      toast.success(result.record.status === "published" ? "Mentve és megjelent a honlapon." : "A piszkozat elmentve.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "A mentés nem sikerült.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!editor?.id || editor.kind === "page") return;
    if (!window.confirm(`Biztosan eltávolítja ezt: ${editor.title}?`)) return;
    setSaving(true);
    try {
      const response = await adminFetch(`/api.php?action=content&id=${encodeURIComponent(editor.id)}`, { method: "DELETE" });
      const result = await response.json() as { error?: string };
      if (!response.ok) throw new Error(result.error || "A törlés nem sikerült.");
      setRecords((current) => editor.isDefault
        ? current.map((item) => item.id === editor.id ? { ...item, status: "archived" as const } : item)
        : current.filter((item) => item.id !== editor.id));
      setEditor(null);
      toast.success("A tartalom lekerült a honlapról.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "A törlés nem sikerült.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto grid max-w-[1500px] gap-5 px-5 pb-16 pt-4 sm:px-8 xl:grid-cols-[260px_360px_minmax(0,1fr)]">
      <aside className="rounded-[1.5rem] bg-[#102f53] p-3 text-white shadow-lg xl:sticky xl:top-28 xl:h-fit">
        <p className="px-3 pb-3 pt-2 text-xs font-black uppercase tracking-[.17em] text-[#91baff]">Mit szeretne kezelni?</p>
        <nav className="grid gap-1 sm:grid-cols-2 xl:grid-cols-1" aria-label="Adminisztrációs kategóriák">
          {kindOrder.map((kind) => {
            const item = kindDetails[kind];
            const Icon = item.icon;
            const count = records.filter((record) => record.kind === kind && record.status !== "archived").length;
            return (
              <button key={kind} type="button" onClick={() => changeKind(kind)} className={`flex items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-black transition ${activeKind === kind ? "bg-white text-[#17365d]" : "text-white/80 hover:bg-white/10 hover:text-white"}`}>
                <Icon className={`size-5 shrink-0 ${activeKind === kind ? "text-[#ef233c]" : "text-[#91baff]"}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">{item.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${activeKind === kind ? "bg-[#edf4fb]" : "bg-white/10"}`}>{count}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="rounded-[1.5rem] bg-white p-4 shadow-sm xl:min-h-[690px]">
        <div className="flex items-center justify-between gap-3 border-b border-[#17365d]/10 px-1 pb-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[.14em] text-[#185adb]">Tartalmak</p>
            <h2 className="mt-1 text-xl font-black text-[#17365d]">{kindDetails[activeKind].label}</h2>
          </div>
          {activeKind !== "page" && (
            <button type="button" onClick={startNew} className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-[#ef233c] text-white shadow-md hover:bg-[#cf1730]" title={`Új ${kindDetails[activeKind].singular}`}>
              <Plus className="size-5" aria-hidden="true" /><span className="sr-only">Új tartalom</span>
            </button>
          )}
        </div>
        <div className="mt-4 grid max-h-[740px] gap-2 overflow-y-auto pr-1">
          {visibleRecords.length === 0 && <p className="rounded-xl bg-[#eef5fc] p-5 text-sm leading-6 text-[#60758b]">Még nincs ilyen tartalom. A piros + gombbal adhat hozzá újat.</p>}
          {visibleRecords.map((record) => (
            <button key={record.id} type="button" onClick={() => setEditor(record)} className={`group flex items-center gap-3 rounded-xl border p-3 text-left transition ${editor?.id === record.id ? "border-[#185adb] bg-[#edf4fb]" : "border-[#17365d]/10 hover:border-[#185adb]/40 hover:bg-[#f7faff]"}`}>
              <span className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${record.status === "published" ? "bg-[#e8f7ef] text-[#087443]" : record.status === "draft" ? "bg-[#fff4dc] text-[#946000]" : "bg-[#f0f1f3] text-[#68717b]"}`}>
                {record.status === "published" ? <Check className="size-5" /> : <Pencil className="size-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-black text-[#17365d]">{record.title || "Névtelen tartalom"}</span>
                <span className="mt-0.5 block text-xs font-semibold text-[#74869a]">{record.status === "published" ? "Megjelent" : record.status === "draft" ? "Piszkozat" : "Elrejtve"}</span>
              </span>
              <Pencil className="size-4 shrink-0 text-[#9aa8b6] group-hover:text-[#185adb]" />
            </button>
          ))}
        </div>
      </section>

      <section className="min-w-0 rounded-[1.5rem] bg-white p-5 shadow-sm sm:p-7 xl:min-h-[690px]">
        {!editor ? (
          <div className="flex min-h-[520px] flex-col items-center justify-center text-center">
            <div className="flex size-20 items-center justify-center rounded-full bg-[#edf4fb]"><Pencil className="size-8 text-[#185adb]" /></div>
            <h2 className="mt-6 text-2xl font-black text-[#17365d]">Válasszon egy tartalmat</h2>
            <p className="mt-3 max-w-md leading-7 text-[#60758b]">A középső oszlopban kattintson a szerkesztendő elemre, vagy hozzon létre újat a + gombbal.</p>
          </div>
        ) : (
          <EditorForm record={editor} setRecord={setEditor} updateData={updateData} saving={saving} onSave={save} onRemove={remove} />
        )}
      </section>
    </div>
  );
}

function EditorForm({ record, setRecord, updateData, saving, onSave, onRemove }: {
  record: ContentRecord;
  setRecord: (record: ContentRecord) => void;
  updateData: (key: string, value: unknown) => void;
  saving: boolean;
  onSave: () => void;
  onRemove: () => void;
}) {
  return (
    <form onSubmit={(event) => { event.preventDefault(); onSave(); }}>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#17365d]/10 pb-5">
        <div>
          <p className="text-xs font-black uppercase tracking-[.14em] text-[#185adb]">{record.id ? "Szerkesztés" : "Új tartalom"}</p>
          <h2 className="mt-1 text-2xl font-black text-[#17365d]">{kindDetails[record.kind].singular[0].toUpperCase() + kindDetails[record.kind].singular.slice(1)}</h2>
        </div>
        <label className="grid gap-1 text-xs font-black uppercase tracking-[.11em] text-[#60758b]">
          Láthatóság
          <select value={record.status} onChange={(event) => setRecord({ ...record, status: event.target.value as ContentStatus })} className="rounded-xl border border-[#17365d]/20 bg-white px-3 py-2.5 text-sm font-bold normal-case tracking-normal text-[#17365d] focus:border-[#185adb] focus:outline-none">
            <option value="published">Megjelenik</option>
            <option value="draft">Piszkozat</option>
            <option value="archived">Elrejtve</option>
          </select>
        </label>
      </div>

      <div className="mt-6 grid gap-5">
        <Field label={record.kind === "athlete" ? "Név" : record.kind === "partner" ? "Támogató neve" : "Cím"} required>
          <input value={record.title} onChange={(event) => setRecord({ ...record, title: event.target.value })} className="admin-input" required />
        </Field>
        {record.kind === "result" && <ResultFields record={record} updateData={updateData} />}
        {record.kind === "athlete" && <AthleteFields record={record} updateData={updateData} />}
        {record.kind === "media" && <MediaFields record={record} updateData={updateData} />}
        {record.kind === "partner" && <PartnerFields record={record} updateData={updateData} />}
        {record.kind === "grant" && <GrantFields record={record} updateData={updateData} />}
        {record.kind === "page" && <PageFields record={record} updateData={updateData} />}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#17365d]/10 pt-5">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#185adb] px-6 py-3.5 font-black text-white shadow-md transition hover:bg-[#123fa2] disabled:cursor-wait disabled:opacity-60">
          {saving ? <LoaderCircle className="size-5 animate-spin" /> : <Save className="size-5" />}{record.status === "published" ? "Mentés és megjelenítés" : "Mentés"}
        </button>
        {record.id && record.kind !== "page" && (
          <button type="button" onClick={onRemove} disabled={saving} className="inline-flex items-center gap-2 rounded-full border border-[#ef233c]/25 px-5 py-3.5 font-black text-[#c51c31] hover:bg-[#fff0f2] disabled:opacity-50"><Trash2 className="size-5" />Eltávolítás</button>
        )}
      </div>
    </form>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return <label className="grid gap-2 text-sm font-black text-[#17365d]"><span>{label}{required && <span className="text-[#ef233c]"> *</span>}</span>{children}{hint && <span className="text-xs font-medium leading-5 text-[#74869a]">{hint}</span>}</label>;
}

function ResultFields({ record, updateData }: FormFieldsProps) {
  return <>
    <div className="grid gap-5 sm:grid-cols-2"><Field label="Dátum"><input className="admin-input" value={value(record, "date")} onChange={(e) => updateData("date", e.target.value)} /></Field><Field label="Helyszín"><input className="admin-input" value={value(record, "place")} onChange={(e) => updateData("place", e.target.value)} /></Field></div>
    <Field label="Rövid összefoglaló"><textarea className="admin-textarea" rows={3} value={value(record, "lead")} onChange={(e) => updateData("lead", e.target.value)} /></Field>
    <Field label="Eredmények" hint="Minden eredmény külön sorba kerüljön."><textarea className="admin-textarea" rows={6} value={linesValue(record, "results")} onChange={(e) => updateData("results", e.target.value.split("\n").map((line) => line.trim()).filter(Boolean))} /></Field>
    <label className="flex items-center gap-3 rounded-xl bg-[#eef5fc] p-4 font-bold text-[#17365d]"><input type="checkbox" className="size-5 accent-[#185adb]" checked={boolValue(record, "featured")} onChange={(e) => updateData("featured", e.target.checked)} />Kiemelt bejegyzés a főoldalon</label>
  </>;
}

function AthleteFields({ record, updateData }: FormFieldsProps) {
  return <>
    <div className="grid gap-5 sm:grid-cols-2"><Field label="Monogram"><input className="admin-input" value={value(record, "initials")} maxLength={5} onChange={(e) => updateData("initials", e.target.value)} /></Field><Field label="Kategória"><input className="admin-input" value={value(record, "category")} onChange={(e) => updateData("category", e.target.value)} /></Field></div>
    <Field label="Versenyszám / szakág"><input className="admin-input" value={value(record, "focus")} onChange={(e) => updateData("focus", e.target.value)} /></Field>
    <Field label="Kiemelt eredmények" hint="Minden eredmény külön sorba kerüljön."><textarea className="admin-textarea" rows={6} value={linesValue(record, "highlights")} onChange={(e) => updateData("highlights", e.target.value.split("\n").map((line) => line.trim()).filter(Boolean))} /></Field>
    <UploadField label="Versenyző fényképe" accept="image/*" currentUrl={value(record, "image")} onUploaded={(url) => updateData("image", url)} />
    {value(record, "image") && <Field label="Kép leírása"><input className="admin-input" value={value(record, "alt")} onChange={(e) => updateData("alt", e.target.value)} /></Field>}
  </>;
}

function MediaFields({ record, updateData }: FormFieldsProps) {
  const mediaType = value(record, "mediaType") || "image";
  return <>
    <div className="grid gap-5 sm:grid-cols-2"><Field label="Típus"><select className="admin-input" value={mediaType} onChange={(e) => updateData("mediaType", e.target.value)}><option value="image">Fénykép</option><option value="video">Videó</option></select></Field><Field label="Kategória"><input className="admin-input" placeholder="pl. Verseny, edzés" value={value(record, "label")} onChange={(e) => updateData("label", e.target.value)} /></Field></div>
    {mediaType === "image" ? <UploadField label="Fénykép" accept="image/*" currentUrl={value(record, "image")} onUploaded={(url) => updateData("image", url)} /> : <>
      <Field label="YouTube-hivatkozás" hint="YouTube-videónál elég ide bemásolni a videó címét."><input className="admin-input" placeholder="https://www.youtube.com/watch?v=…" value={value(record, "videoUrl")} onChange={(e) => updateData("videoUrl", e.target.value)} /></Field>
      <UploadField label="Vagy töltsön fel videófájlt" accept="video/mp4,video/webm,video/quicktime" currentUrl={value(record, "videoUrl").startsWith("/uploads/") ? value(record, "videoUrl") : ""} onUploaded={(url) => updateData("videoUrl", url)} />
    </>}
    <Field label="Kép vagy videó rövid leírása"><input className="admin-input" value={value(record, "alt")} onChange={(e) => updateData("alt", e.target.value)} /></Field>
    <Field label="Képaláírás"><textarea className="admin-textarea" rows={2} value={value(record, "caption")} onChange={(e) => updateData("caption", e.target.value)} /></Field>
    <div className="grid gap-5 sm:grid-cols-2"><Field label="Készítő / forrás"><input className="admin-input" value={value(record, "credit")} onChange={(e) => updateData("credit", e.target.value)} /></Field><Field label="Forrás hivatkozása"><input className="admin-input" type="url" value={value(record, "source")} onChange={(e) => updateData("source", e.target.value)} /></Field></div>
  </>;
}

function PartnerFields({ record, updateData }: FormFieldsProps) {
  return <>
    <Field label="Rövid jelölés"><input className="admin-input" placeholder="pl. MKKSZ" value={value(record, "mark")} onChange={(e) => updateData("mark", e.target.value)} /></Field>
    <Field label="Szerep / támogatás"><textarea className="admin-textarea" rows={3} value={value(record, "role")} onChange={(e) => updateData("role", e.target.value)} /></Field>
    <Field label="Honlap"><input className="admin-input" type="url" value={value(record, "website")} onChange={(e) => updateData("website", e.target.value)} /></Field>
    <UploadField label="Logó" accept="image/*" currentUrl={value(record, "image")} onUploaded={(url) => updateData("image", url)} />
  </>;
}

function GrantFields({ record, updateData }: FormFieldsProps) {
  return <>
    <div className="grid gap-5 sm:grid-cols-2"><Field label="Év és állapot"><input className="admin-input" placeholder="2026 • Aktív program" value={value(record, "badge")} onChange={(e) => updateData("badge", e.target.value)} /></Field><Field label="Iktatószám"><input className="admin-input" value={value(record, "reference")} onChange={(e) => updateData("reference", e.target.value)} /></Field></div>
    <Field label="A program célja"><textarea className="admin-textarea" rows={5} value={value(record, "goal")} onChange={(e) => updateData("goal", e.target.value)} /></Field>
    <Field label="Támogatott területek" hint="Minden terület külön sorba kerüljön."><textarea className="admin-textarea" rows={5} value={linesValue(record, "areas")} onChange={(e) => updateData("areas", e.target.value.split("\n").map((line) => line.trim()).filter(Boolean))} /></Field>
    <Field label="Kapcsolódó hivatkozás"><input className="admin-input" type="url" value={value(record, "website")} onChange={(e) => updateData("website", e.target.value)} /></Field>
  </>;
}

function PageFields({ record, updateData }: FormFieldsProps) {
  return <>
    <Field label="Kis felső cím"><input className="admin-input" value={value(record, "eyebrow")} onChange={(e) => updateData("eyebrow", e.target.value)} /></Field>
    <Field label="Bevezető leírás"><textarea className="admin-textarea" rows={4} value={value(record, "description")} onChange={(e) => updateData("description", e.target.value)} /></Field>
    <Field label="Oldal törzsszövege" hint="Ahol van külön bemutatkozó szöveg, itt módosítható. Az üres mező nem változtatja meg a többi tartalmi kártyát."><textarea className="admin-textarea" rows={9} value={value(record, "body")} onChange={(e) => updateData("body", e.target.value)} /></Field>
  </>;
}

type FormFieldsProps = { record: ContentRecord; updateData: (key: string, value: unknown) => void };

function UploadField({ label, accept, currentUrl, onUploaded }: { label: string; accept: string; currentUrl: string; onUploaded: (url: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function upload(file?: File) {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await adminFetch("/api.php?action=upload", { method: "POST", body: form });
      const result = await response.json() as { url?: string; error?: string };
      if (!response.ok || !result.url) throw new Error(result.error || "A feltöltés nem sikerült.");
      onUploaded(result.url);
      toast.success("A fájl feltöltése sikerült.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "A feltöltés nem sikerült.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="grid gap-2">
      <span className="text-sm font-black text-[#17365d]">{label}</span>
      <input ref={inputRef} type="file" accept={accept} className="sr-only" onChange={(event) => upload(event.target.files?.[0])} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="flex min-h-28 items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#185adb]/30 bg-[#f5f9fd] px-5 py-5 font-black text-[#185adb] transition hover:border-[#185adb] hover:bg-[#edf4fb] disabled:opacity-60">
        {uploading ? <LoaderCircle className="size-6 animate-spin" /> : currentUrl ? <ImagePlus className="size-6" /> : <Upload className="size-6" />}
        {uploading ? "Feltöltés…" : currentUrl ? "Másik fájl feltöltése" : "Fájl kiválasztása"}
      </button>
      {currentUrl && <p className="truncate rounded-lg bg-[#edf4fb] px-3 py-2 text-xs font-semibold text-[#49647e]">{currentUrl}</p>}
    </div>
  );
}
